// 2026-09-23 - FormuLearning - Step 1 대조약·허가 품목 목록 2차 필터 바 (이슈 #73 P3, M901-08)
// 파일 위치: frontend/src/features/step1/RldFilterBar.tsx
//
// 칩은 **켜면 포함 · 끄면 제외**(M901-08). 판정은 `rldFilter.ts`, 총중량 조회는 `RldSection` 이 한다.

import { useState } from 'react'
import { WEIGHT_BANDS, isFiltered, type RldFilter } from './rldFilter'

// 성분 칩을 처음에 보여 줄 개수 — 복합제가 많은 성분은 수십 종이다.
const INGREDIENT_CHIPS_SHOWN = 12

interface Props {
  filter: RldFilter
  onChange: (next: RldFilter) => void
  onReset: () => void
  ingredients: { name: string; count: number }[]
  shown: number
  total: number
  /** 총중량 필터용 상세 조회 진행(조회할 품목이 없으면 null) */
  weightProgress: { done: number; total: number } | null
  hasReference: boolean
}

function Chip({ on, label, onClick, title }: { on: boolean; label: string; onClick: () => void; title?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={on}
      className="rounded px-1.5 py-0.5 text-[10px] transition-colors"
      style={{
        borderWidth: 1,
        borderColor: on ? 'var(--border-purple)' : 'var(--border-subtle)',
        color: on ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
        backgroundColor: on ? 'var(--bg-row-active)' : 'transparent',
        textDecoration: on ? 'none' : 'line-through',
      }}
    >
      {label}
    </button>
  )
}

export default function RldFilterBar({ filter, onChange, onReset, ingredients, shown, total, weightProgress, hasReference }: Props) {
  const [allIngredients, setAllIngredients] = useState(false)
  const set = (patch: Partial<RldFilter>) => onChange({ ...filter, ...patch })
  const toggleIngredient = (name: string) => set({
    excludedIngredients: filter.excludedIngredients.includes(name)
      ? filter.excludedIngredients.filter((n) => n !== name)
      : [...filter.excludedIngredients, name],
  })
  const toggleBand = (key: RldFilter['weightBands'][number]) => set({
    weightBands: filter.weightBands.includes(key) ? filter.weightBands.filter((k) => k !== key) : [...filter.weightBands, key],
  })
  const chips = allIngredients ? ingredients : ingredients.slice(0, INGREDIENT_CHIPS_SHOWN)
  const label = 'w-16 shrink-0 text-[10px] text-text-secondary'
  const input = 'w-40 rounded border bg-transparent px-1.5 py-0.5 text-[10px] text-text-body'

  return (
    <div className="mb-2 space-y-1.5 rounded-md border px-3 py-2" style={{ borderColor: 'var(--border-subtle)' }}>
      <div className="flex items-center justify-between text-[10px] text-text-secondary">
        <span>Filter — {shown.toLocaleString()} / {total.toLocaleString()}</span>
        {isFiltered(filter) && (
          <button type="button" onClick={onReset} className="underline" style={{ color: 'var(--color-accent-teal)' }}>
            초기화
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className={label}>주성분 개수</span>
        <Chip on={filter.single} label="단일성분" onClick={() => set({ single: !filter.single })} />
        <Chip on={filter.combo} label="복합제" onClick={() => set({ combo: !filter.combo })} />
        <span className="mx-1 text-text-muted">·</span>
        <Chip on={filter.cancelled} label="취소·만료" onClick={() => set({ cancelled: !filter.cancelled })}
              title="허가가 취소·만료된 품목 — 기본 제외, 켜면 함께 봅니다(M1002-20)" />
        {hasReference && (
          <>
            <span className="mx-1 text-text-muted">·</span>
            <Chip on={filter.referenceOnly} label="대조약만" onClick={() => set({ referenceOnly: !filter.referenceOnly })} />
          </>
        )}
      </div>

      {ingredients.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={label}>주성분</span>
          {chips.map(({ name, count }) => (
            <Chip key={name} on={!filter.excludedIngredients.includes(name)} label={`${name} ${count}`}
                  onClick={() => toggleIngredient(name)} title="끄면 이 성분을 가진 품목을 제외합니다" />
          ))}
          {ingredients.length > INGREDIENT_CHIPS_SHOWN && (
            <button type="button" onClick={() => setAllIngredients((v) => !v)} className="text-[10px] underline text-text-muted">
              {allIngredients ? '접기' : `+${ingredients.length - INGREDIENT_CHIPS_SHOWN}`}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <span className={label}>총중량 mg</span>
        <Chip on={filter.weightOn} label={filter.weightOn ? '사용 중' : '사용'} onClick={() => set({ weightOn: !filter.weightOn })}
              title="켜면 다른 필터로 좁혀진 품목의 상세를 조회해 총중량으로 거릅니다" />
        {filter.weightOn && WEIGHT_BANDS.map(({ key, label: l }) => (
          <Chip key={key} on={filter.weightBands.includes(key)} label={l} onClick={() => toggleBand(key)} />
        ))}
        {filter.weightOn && weightProgress && (
          <span className="text-[10px] text-text-muted">
            총중량 조회 {weightProgress.done}/{weightProgress.total}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className={label}>제조사</span>
        <input value={filter.maker} onChange={(e) => set({ maker: e.target.value })} placeholder="제조사 검색"
               aria-label="제조사 검색" className={input} style={{ borderColor: 'var(--border-subtle)' }} />
        <span className="ml-2 w-12 shrink-0 text-[10px] text-text-secondary">약품명</span>
        <input value={filter.name} onChange={(e) => set({ name: e.target.value })} placeholder="약품명 검색"
               aria-label="약품명 검색" className={input} style={{ borderColor: 'var(--border-subtle)' }} />
      </div>
    </div>
  )
}
